import { useState } from "react";
import { alpha3ToAlpha2 } from "../utils/kansalaisuudet";

import { createGlobalStyle } from "styled-components";
import { SIDE_NAV_WIDTH_PIXELS } from "../layout/constants";
import styled from "styled-components";
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Button,
  TextInput,
  SingleSelect,
  Dropdown,
  DropdownItem,
  Text,
  Checkbox,
  DateInput,
} from "suomifi-ui-components";

import type { DokumenttiTyyppi } from "../types";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";
import { useTranslation } from "react-i18next";
import { getKansalaisuusOptions } from "../utils/kansalaisuudet";
import { useManualVerificationMutation } from "../api/backendApi";
import KuvatModal from "./KuvatModal";

/**
 * Manuaaliprosessi-lomake virkailijalle (workshop E1 + D9).
 *
 * Virkailija avaa lomakkeen "Todenna henkilöllisyys" -painikkeesta
 * käyttäjäkortilla. Syöttää passi-/henkilökorttitiedot käsin ja merkitsee
 * skannattujen kuvien vastaanoton. Vahvistus → käyttäjä todennettu +
 * manuaaliprosessillaLuotu-flag asetetaan, jotta tieto näkyy myöhemmin
 * audit-trailissa ja Henkilöllisyyden todentaminen -sektiossa.
 *
 * Skannauskuvat tallennetaan rajattuna versiona (kuten käyttäjän selfie/
 * passikuva), ei täysikokoisina (workshop E5). Tämä mock näyttää vain
 * "kuvat liitetty" -tilan.
 */

interface Props {
  visible: boolean;
  kayttajaSahkoposti: string;
  kayttajaId: string;
  onConfirm: (tiedot: ManuaaliprosessiTiedot) => void;
  onCancel: () => void;
}

export interface ManuaaliprosessiTiedot {
  etunimi: string;
  sukunimi: string;
  syntymaaika: string;
  asiakirjaTyyppi: DokumenttiTyyppi;
  maakoodi: string;
  asiakirjaNumero: string;
  vanhenee: string;
  kuvatLiitetty: boolean;
}

const Description = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.m};
  margin-bottom: ${({ theme }) => theme.spacing.m};
  margin-left: ${({ theme }) => theme.spacing.m};
  margin-right: ${({ theme }) => theme.spacing.m};
`;

const KokoLeveys = styled.div`
  grid-column: 1 / -1;
`;

const KuvatBox = styled.div`
  background: ${({ theme }) => theme.colors.depthLight3};
  border: 1px dashed ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const KuvatTitle = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const KuvatHint = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const asiakirjaOptions: { label: string; value: DokumenttiTyyppi }[] = [
  { label: "Passi", value: "passi" },
  { label: "Henkilökortti", value: "henkilökortti" },
];

const KuvatError = styled.div`
  color: ${({ theme }) => theme.colors.alertBase};
  font-size: 0.875rem;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ModalWidthStyle = createGlobalStyle`
  .fi-modal_base {
    z-index: 9999 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-end !important;
    padding-left: 207px !important;
    box-sizing: border-box !important;
  }

  .fi-modal_base .fi-modal,
  .fi-modal_base .fi-modal--no-scroll {
    width: 70vw !important;
    max-width: 700px !important;
    transform: translateX(${SIDE_NAV_WIDTH_PIXELS / 2}px) !important;
  }
`;

// Add date conversion helpers
function ddmmyyyyToYyyymmdd(dateString: string): string {
  if (!dateString.trim()) return "";
  const regex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
  const match = dateString.match(regex);
  if (match) {
    const day = match[1].padStart(2, "0"); // ← Pad to 2 digits
    const month = match[2].padStart(2, "0"); // ← Pad to 2 digits
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  return dateString;
}

function yyyymmddToDdmmyyyy(dateString: string): string {
  if (!dateString.trim()) return "";
  const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = dateString.match(regex);
  if (match) {
    return `${match[3]}.${match[2]}.${match[1]}`;
  }
  return dateString;
}

export default function ManuaaliprosessiModal({
  visible,
  kayttajaSahkoposti,
  kayttajaId,
  onConfirm,
  onCancel,
}: Props) {
  const [manualVerification, { isLoading }] = useManualVerificationMutation();
  const { t, i18n } = useTranslation(["manuaaliProsessi", "common"]);
  const [virheViesti, setVirheViesti] = useState<string>("");
  const kansalaisuusOptions = getKansalaisuusOptions(i18n.language).map(
    (option) => ({
      labelText: option.label,
      uniqueItemId: option.value,
    }),
  );
  const sukupuoliOptions = [
    { label: t("common:sukupuolet.nainen"), value: "FEMALE" },
    { label: t("common:sukupuolet.mies"), value: "MALE" },
    { label: t("common:sukupuolet.muu"), value: "OTHER" },
    { label: t("common:sukupuolet.eiIlmoitettu"), value: "UNDISCLOSED" },
  ];
  const [etunimi, setEtunimi] = useState("");
  const nameRegex = /^[A-Z0-9 ]+$/;
  const [sukunimi, setSukunimi] = useState("");
  const [syntymaaika, setSyntymaaika] = useState("");
  const [asiakirjaTyyppi, setAsiakirjaTyyppi] =
    useState<DokumenttiTyyppi>("passi");
  const [maakoodi, setMaakoodi] = useState("");
  const [asiakirjaNumero, setAsiakirjaNumero] = useState("");
  const [vanhenee, setVanhenee] = useState("");
  const [kuvatLiitetty, setKuvatLiitetty] = useState(false);
  const [virhe, setVirhe] = useState(false);
  const [sukupuoli, setSukupuoli] = useState("UNDISCLOSED");
  const [kansalaisuus, setKansalaisuus] = useState("");
  const [noGivenNameChecked, setNoGivenNameChecked] = useState(false);
  const [noSurnameChecked, setNoSurnameChecked] = useState(false);
  const [kuvatModalAuki, setKuvatModalAuki] = useState(false);
  const [nameErrorText, setNameErrorText] = useState("");
  const [kuvat, setKuvat] = useState<{
    dokumenttiEtupuoli?: File;
    dokumenttiTakapuoli?: File;
    selfie?: File;
  }>({});
  function isValidDate(dateString: string): boolean {
    if (!dateString.trim()) return false;

    // First convert dd.mm.yyyy to yyyy-mm-dd if needed
    const convertedDate = ddmmyyyyToYyyymmdd(dateString);

    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(convertedDate)) {
      return false;
    }

    const date = new Date(convertedDate + "T00:00:00Z");
    const timestamp = date.getTime();

    if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
      return false;
    }

    // Check if the date string matches what JavaScript parsed
    const [year, month, day] = convertedDate.split("-").map(Number);
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() + 1 === month &&
      date.getUTCDate() === day
    );
  }

  async function valmis() {
    // Clear previous errors
    setVirhe(false);
    setNameErrorText("");
    setVirheViesti("");
    const errors: string[] = [];

    // Regex for validating etunimi or sukunimi
    const nameRegex = /^[A-Za-z0-9\- ]+$/;

    // Validate names (at least one must be present)
    // Validate etunimi (mandatory, must match regex)
    // Validate names: EITHER etunimi OR sukunimi must be present
    if (noGivenNameChecked && noSurnameChecked) {
      const errorMsg = t("validation.name_required");
      errors.push(errorMsg);
      setNameErrorText(errorMsg);
    } else {
      // If etunimi is not skipped, validate it
      if (!noGivenNameChecked && !etunimi.trim()) {
        errors.push(t("validation.etunimi_required"));
      } else if (!noGivenNameChecked && !nameRegex.test(etunimi.trim())) {
        errors.push(t("validation.etunimi_invalid"));
      }

      // If sukunimi is not skipped, validate it
      if (!noSurnameChecked && !sukunimi.trim()) {
        errors.push(t("validation.sukunimi_required"));
      } else if (!noSurnameChecked && !nameRegex.test(sukunimi.trim())) {
        errors.push(t("validation.sukunimi_invalid"));
      }
    }

    // Validate birth date format (YYYY-MM-DD)
    if (!syntymaaika.trim()) {
      errors.push(t("validation.syntymaaika_required"));
    } else if (!isValidDate(syntymaaika.trim())) {
      errors.push(t("validation.syntymaaika_format"));
    }

    // Validate nationality (must be selected from dropdown - 3 letter code)
    if (!kansalaisuus.trim()) {
      errors.push(t("validation.kansalaisuus_required"));
    } else if (kansalaisuus.length !== 3) {
      errors.push(t("validation.kansalaisuus_invalid"));
    }

    // Validate issuing country (must be selected from dropdown - 3 letter code)
    if (!maakoodi.trim()) {
      errors.push(t("validation.maakoodi_required"));
    } else if (maakoodi.length !== 3) {
      errors.push(t("validation.maakoodi_invalid"));
    }

    // Validate expiry date format (YYYY-MM-DD)
    if (!vanhenee.trim()) {
      errors.push(t("validation.vanhenee_required"));
    } else if (!isValidDate(vanhenee.trim())) {
      errors.push(t("validation.vanhenee_format"));
    }

    // Validate document number
    if (!asiakirjaNumero.trim()) {
      errors.push(t("validation.asiakirjaNumero_required"));
    }

    // Validate at least one picture is uploaded
    if (
      !kuvat.dokumenttiEtupuoli &&
      !kuvat.dokumenttiTakapuoli &&
      !kuvat.selfie
    ) {
      errors.push(t("validation.kuvat_required"));
    }

    // If there are errors, show them and stop
    if (errors.length > 0) {
      console.error("Validation errors:", errors);
      setVirhe(true);
      // Optionally show error messages to user
      return;
    }

    const tiedot = {
      etunimi: noGivenNameChecked ? "" : etunimi.trim(),
      sukunimi: noSurnameChecked ? "" : sukunimi.trim(),
      syntymaaika: ddmmyyyyToYyyymmdd(syntymaaika.trim()),
      asiakirjaTyyppi,
      maakoodi: alpha3ToAlpha2(maakoodi),
      asiakirjaNumero: asiakirjaNumero.trim(),
      vanhenee: ddmmyyyyToYyyymmdd(vanhenee.trim()),
      sukupuoli,
      kansalaisuus: alpha3ToAlpha2(kansalaisuus),
      kuvatLiitetty,
      dokumenttiEtupuoli: kuvat.dokumenttiEtupuoli,
      dokumenttiTakapuoli: kuvat.dokumenttiTakapuoli,
      selfie: kuvat.selfie,
    };

    try {
      await manualVerification({
        kayttajaId,
        ...tiedot,
      }).unwrap();

      onConfirm(tiedot);
      nollaa();
    } catch (error) {
      console.error("Error submitting manual verification:", error);
      setVirhe(true);

      // ✅ Updated to match actual error structure
      const apiError = error as {
        data?: {
          errorMessage?: string;
          message?: string;
          error?: string;
          status?: number;
        };
        message?: string;
        status?: number;
      };
      const statusCode = apiError?.status || apiError?.data?.status || 500;
      const errorMessage =
        apiError?.data?.errorMessage ||
        apiError?.data?.message ||
        apiError?.data?.error ||
        apiError?.message ||
        t("virhe.tuntematon_virhe");

      if (statusCode === 409) {
        // Duplicate document error
        setVirheViesti(`${t("virhe.duplikaatti_asiakirja")}: ${errorMessage}`);
      } else if (statusCode === 403) {
        // Permission error
        setVirheViesti(errorMessage);
      } else {
        setVirheViesti(errorMessage);
      }
    }
  }

  function nollaa() {
    setEtunimi("");
    setSukunimi("");
    setSyntymaaika("");
    setAsiakirjaTyyppi("passi");
    setMaakoodi("");
    setAsiakirjaNumero("");
    setVanhenee("");
    setSukupuoli("UNDISCLOSED");
    setKansalaisuus("");
    setKuvatLiitetty(false);
    setVirhe(false);
    setVirheViesti("");
    setNameErrorText("");
  }

  function peruuta() {
    nollaa();
    onCancel();
  }

  useModaaliBackdropSulku(visible, peruuta);

  return (
    <>
      {visible && <ModalWidthStyle />}
      <Modal
        appElementId="root"
        visible={visible}
        onEscKeyDown={peruuta}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("titles.modal")}</ModalTitle>

          {/* ✅ Show error message at top of modal */}
          {virheViesti && (
            <div
              style={{
                background: "#FEE8E8",
                border: "1px solid #D32F2F",
                borderRadius: "4px",
                padding: "12px",
                marginBottom: "16px",
                color: "#D32F2F",
              }}
            >
              <strong>{t("virhe.otsikko")}:</strong> {virheViesti}
            </div>
          )}

          <Description>
            {t("tekstit.kuvaus1")} ({kayttajaSahkoposti}) {t("tekstit.kuvaus2")}
          </Description>

          <Grid>
            <div>
              <TextInput
                labelText={t("labels.etunimi")}
                value={etunimi}
                onChange={(value) =>
                  setEtunimi(typeof value === "string" ? value : "")
                }
                disabled={noGivenNameChecked}
                status={
                  virhe && noGivenNameChecked && noSurnameChecked // ← Check if BOTH are unchecked
                    ? "error"
                    : virhe &&
                      !noGivenNameChecked &&
                      (!etunimi.trim() || !nameRegex.test(etunimi.trim()))
                    ? "error"
                    : "default"
                }
                statusText={
                  virhe && noGivenNameChecked && noSurnameChecked
                    ? t("validation.no_name") // ← Show this when both unchecked
                    : virhe && !noGivenNameChecked && !etunimi.trim()
                    ? t("validation.etunimi_required")
                    : virhe &&
                      !noGivenNameChecked &&
                      etunimi.trim() &&
                      !nameRegex.test(etunimi.trim())
                    ? t("validation.etunimi_invalid")
                    : ""
                }
                fullWidth
              />
              <Checkbox
                checked={noGivenNameChecked}
                onChange={(e) => {
                  setNoGivenNameChecked((e.target as HTMLInputElement).checked);
                  if ((e.target as HTMLInputElement).checked) setEtunimi("");
                }}
              >
                {t("labels.no_given_name")}
              </Checkbox>
            </div>

            <div>
              <TextInput
                labelText={t("labels.sukunimi")}
                value={sukunimi}
                onChange={(value) =>
                  setSukunimi(typeof value === "string" ? value : "")
                }
                disabled={noSurnameChecked}
                status={
                  virhe && noGivenNameChecked && noSurnameChecked // ← Check if BOTH are unchecked
                    ? "error"
                    : virhe &&
                      !noSurnameChecked &&
                      (!sukunimi.trim() || !nameRegex.test(sukunimi.trim()))
                    ? "error"
                    : "default"
                }
                statusText={
                  virhe && noGivenNameChecked && noSurnameChecked
                    ? t("validation.no_name") // ← Show this when both unchecked
                    : virhe && !noSurnameChecked && !sukunimi.trim()
                    ? t("validation.sukunimi_required")
                    : virhe &&
                      !noSurnameChecked &&
                      sukunimi.trim() &&
                      !nameRegex.test(sukunimi.trim())
                    ? t("validation.sukunimi_invalid")
                    : ""
                }
                fullWidth
              />
              <Checkbox
                checked={noSurnameChecked}
                onChange={(e) => {
                  setNoSurnameChecked((e.target as HTMLInputElement).checked);
                  if ((e.target as HTMLInputElement).checked) setSukunimi("");
                }}
              >
                {t("labels.no_surname")}
              </Checkbox>
            </div>
            <DateInput
              labelText={t("labels.syntymaAika")}
              value={syntymaaika}
              datePickerEnabled
              onChange={({ value }: { value: string; date: Date }) =>
                setSyntymaaika(value ?? "")
              }
              status={
                virhe &&
                (!syntymaaika.trim() ||
                  !isValidDate(ddmmyyyyToYyyymmdd(syntymaaika.trim())))
                  ? "error"
                  : "default"
              }
              statusText={
                virhe && !syntymaaika.trim()
                  ? t("validation.syntymaaika_required")
                  : virhe &&
                    !isValidDate(ddmmyyyyToYyyymmdd(syntymaaika.trim()))
                  ? t("validation.syntymaaika_format")
                  : ""
              }
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              fullWidth
            />
            <Dropdown
              labelText={t("labels.sukupuoli")}
              value={sukupuoli}
              onChange={(value: string) => setSukupuoli(value)}
              fullWidth
            >
              {sukupuoliOptions.map((o) => (
                <DropdownItem key={o.value} value={o.value}>
                  {o.label}
                </DropdownItem>
              ))}
            </Dropdown>
            <SingleSelect
              labelText={t("labels.kansalaisuus")}
              items={kansalaisuusOptions}
              value={kansalaisuus}
              status={virhe && !kansalaisuus.trim() ? "error" : "default"}
              statusText={
                virhe && !kansalaisuus.trim()
                  ? t("validation.kansalaisuus_required")
                  : ""
              }
              onItemSelect={(item: string | null) => {
                setKansalaisuus(item ?? "");
              }}
              clearButtonLabel={t("common:tyhjenna")}
              allowItemAddition={false}
              ariaOptionsAvailableText={t("common:valitseVaihtoehto")}
              noItemsText={t("common:eiTuloksia")}
              fullWidth
            />

            <Dropdown
              labelText={t("labels.asiakirjaTyyppi")}
              value={asiakirjaTyyppi}
              onChange={(value: string | undefined) =>
                setAsiakirjaTyyppi(value as DokumenttiTyyppi)
              }
              fullWidth
            >
              {asiakirjaOptions.map((o) => (
                <DropdownItem key={o.value} value={o.value}>
                  {o.label}
                </DropdownItem>
              ))}
            </Dropdown>
            <SingleSelect
              labelText={t("labels.myontajaMaa")}
              value={maakoodi}
              status={virhe && !maakoodi.trim() ? "error" : "default"}
              statusText={
                virhe && !maakoodi.trim()
                  ? t("validation.maakoodi_required")
                  : ""
              }
              onItemSelect={(uniqueItemId: string | null) => {
                setMaakoodi(uniqueItemId ?? "");
              }}
              items={kansalaisuusOptions}
              allowItemAddition={false}
              clearButtonLabel={t("common:tyhjenna")}
              noItemsText={t("common:eiTuloksia")}
              ariaOptionsAvailableText={t("common:valitseVaihtoehto") ?? ""}
              fullWidth
            />

            <DateInput
              labelText={t("labels.voimassa")}
              value={vanhenee}
              datePickerEnabled
              onChange={({ value }: { value: string; date: Date }) =>
                setVanhenee(value ?? "")
              }
              status={
                virhe && (!vanhenee.trim() || !isValidDate(vanhenee.trim()))
                  ? "error"
                  : "default"
              }
              statusText={
                virhe && !vanhenee.trim()
                  ? t("validation.vanhenee_required")
                  : virhe && !isValidDate(vanhenee.trim())
                  ? t("validation.vanhenee_format")
                  : ""
              }
              fullWidth
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date(2100, 0, 1)}
            />

            <TextInput
              labelText={t("labels.asiakirjaNumero")}
              value={asiakirjaNumero}
              onChange={(value) =>
                setAsiakirjaNumero(typeof value === "string" ? value : "")
              }
              status={virhe && !asiakirjaNumero.trim() ? "error" : "default"}
              statusText={
                virhe && !asiakirjaNumero.trim()
                  ? t("validation.asiakirjaNumero_required")
                  : ""
              }
              fullWidth
            />
          </Grid>

          <KuvatBox>
            <KuvatTitle>{t("titles.kuvat")}</KuvatTitle>
            <KuvatHint>{t("tekstit.kuvat")}</KuvatHint>
            <div style={{ marginTop: "0.75rem" }}>
              <Button onClick={() => setKuvatModalAuki(true)}>
                {Object.keys(kuvat).length > 0
                  ? t("kuvat.liitetty")
                  : t("kuvat.liita")}
              </Button>
            </div>
            {virhe &&
              !kuvat.dokumenttiEtupuoli &&
              !kuvat.dokumenttiTakapuoli &&
              !kuvat.selfie && (
                <KuvatError>{t("validation.kuvat_required")}</KuvatError>
              )}
          </KuvatBox>
        </ModalContent>
        <ModalFooter>
          <Button onClick={valmis}>{t("buttons.vahvista")}</Button>
          <Button variant="secondary" onClick={peruuta}>
            {t("buttons.peruuta")}
          </Button>
        </ModalFooter>
        <KuvatModal
          visible={kuvatModalAuki}
          onConfirm={(files) => {
            setKuvat(files);
            setKuvatLiitetty(true);
            setKuvatModalAuki(false);
          }}
          onCancel={() => setKuvatModalAuki(false)}
        />
      </Modal>
    </>
  );
}
